// src/services/apaleo/folios.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import type { ReservationModel, FolioModel, LogFn } from './types';
import type { ApaleoClient } from './client-contract';
import { errorMessage } from './config';

const part: ThisType<ApaleoClient> = {
async addFolioCharge(
  folioId: string,
  body: Record<string, unknown>,
  idempotencyKey: string | null = null,
): Promise<unknown> {
  if (!folioId) throw new Error("addFolioCharge: folioId is required");
  if (!body) throw new Error("addFolioCharge: body is required");

  const key =
    idempotencyKey ||
    (typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `adapthub-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  return await this._fetch(`/finance/v1/folio-actions/${folioId}/charges`, {
    method: "POST",
    headers: { "Idempotency-Key": key },
    body: JSON.stringify(body),
  });
},

async fetchOpenFolioForReservation(
  reservationId: string,
): Promise<FolioModel | null> {
  if (!reservationId) return null;
  try {
    // IMPORTANT: the Apaleo REST API uses snake_case parameter names
    // (`reservation_ids`, `only_main`), NOT camelCase. Using `reservationIds`
    // (camelCase) is silently ignored, so the response would contain all
    // open folios for the tenant, and `folios[0]` would be whichever folio
    // Apaleo returns first in its default sort order (which is the
    // secondary folio YRDYJEYJ-1-2, NOT the main YRDYJEYJ-1-1).
    //
    // `only_main=true` further filters to just the primary folio (the one
    // with `isMainFolio: true`), which is the correct target for posting
    // the city tax charges.
    // Direct GET on the canonical main-folio id (reservationId-1). Faster
    // and unambiguous vs. the previous list-based query that occasionally
    // returned folios from other bookings in the tenant.
    const folio = (await this._fetch(
      `/finance/v1/folios/${encodeURIComponent(reservationId)}-1?expand=charges`,
    )) as FolioModel | null;
    const rid = folio?.reservation?.id;
    if (rid && rid !== reservationId) {
      return null;
    }
    return folio || null;
  } catch (e) {
    // 404 is expected for reservations without a main folio (e.g. cancelled
    // before check-in). Treat as a soft "no folio".
    const msg = errorMessage(e);
    if (/API Fehler 404\b/.test(msg)) return null;
    return null;
  }
},

async fetchFoliosForReservation(reservationId: string): Promise<FolioModel[]> {
  if (!reservationId) return [];
  try {
    const data = (await this._fetch(
      `/finance/v1/folios?reservationIds=${encodeURIComponent(reservationId)}`
      + `&expand=charges&expand=allowances&expand=payments&expand=allowedActions`,
    )) as { folios?: FolioModel[] } | boolean | null;
    // 204 No Content is normalized to `true` by _fetch — no folios exist.
    if (!data || data === true) return [];
    return Array.isArray(data.folios) ? data.folios : [];
  } catch (e) {
    const msg = errorMessage(e);
    if (/API Fehler 404\b/.test(msg)) return [];
    throw e;
  }
},

async addFolioPayment(
  folioId: string,
  body: Record<string, unknown>,
  idempotencyKey: string | null = null,
): Promise<unknown> {
  if (!folioId) throw new Error("addFolioPayment: folioId is required");
  if (!body) throw new Error("addFolioPayment: body is required");

  const headers: Record<string, string> = idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {};
  return await this._fetch(`/finance/v1/folios/${encodeURIComponent(folioId)}/payments`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
},

async fetchPaymentAccountsForReservation(reservationId: string): Promise<unknown[]> {
  if (!reservationId) return [];
  try {
    const data = (await this._fetch(
      `/booking/v1/payment-accounts?reservationIds=${encodeURIComponent(reservationId)}`,
    )) as { paymentAccounts?: unknown[] } | boolean | null;
    // 204 No Content is normalized to `true` by _fetch — no accounts exist.
    if (!data || data === true) return [];
    return Array.isArray(data.paymentAccounts) ? data.paymentAccounts : [];
  } catch (e) {
    const msg = errorMessage(e);
    if (/API Fehler 404\b/.test(msg)) return [];
    throw e;
  }
},

async addFolioPaymentByPaymentAccount(
  folioId: string,
  body: Record<string, unknown>,
  idempotencyKey: string | null = null,
): Promise<unknown> {
  if (!folioId) throw new Error("addFolioPaymentByPaymentAccount: folioId is required");
  if (!body) throw new Error("addFolioPaymentByPaymentAccount: body is required");

  const headers: Record<string, string> = idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {};
  return await this._fetch(
    `/finance/v1/folios/${encodeURIComponent(folioId)}/payments/by-payment-account`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    },
  );
},

async closeFolio(folioId: string): Promise<unknown> {
  if (!folioId) throw new Error("closeFolio: folioId is required");
  return await this._fetch(`/finance/v1/folio-actions/${encodeURIComponent(folioId)}/close`, {
    method: "PUT",
  });
},

async reopenFolio(folioId: string): Promise<unknown> {
  if (!folioId) throw new Error("reopenFolio: folioId is required");
  return await this._fetch(`/finance/v1/folio-actions/${encodeURIComponent(folioId)}/reopen`, {
    method: "PUT",
  });
},

async postAllowanceForCharge(
  folioId: string,
  chargeId: string,
  body: Record<string, unknown>,
  idempotencyKey: string | null = null,
): Promise<unknown> {
  if (!folioId) throw new Error("postAllowanceForCharge: folioId is required");
  if (!chargeId) throw new Error("postAllowanceForCharge: chargeId is required");
  if (!body) throw new Error("postAllowanceForCharge: body is required");

  const key =
    idempotencyKey ||
    (typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `adapthub-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  return await this._fetch(
    `/finance/v1/folio-actions/${encodeURIComponent(folioId)}/charges/${encodeURIComponent(chargeId)}/allowances`,
    {
      method: "POST",
      headers: { "Idempotency-Key": key },
      body: JSON.stringify(body),
    },
  );
},

_splitPerNightGross(grossPerNight: unknown): {
  net: number;
  vat: number;
  cityTax: number;
  accommodation: number;
} {
  const g = Number(grossPerNight);
  if (!Number.isFinite(g) || g <= 0) {
    return { net: 0, vat: 0, cityTax: 0, accommodation: 0 };
  }
  // 1.15025 = 1.07 (VAT) * 1.075 (city tax). VAT applies to (net + cityTax).
  const net = g / 1.15025;
  const cityTax = net * 0.075;
  const vat = (net + cityTax) * 0.07;
  const accommodation = net * 1.07; // net + 7% VAT on net
  return {
    net: Number(net.toFixed(2)),
    vat: Number(vat.toFixed(2)),
    cityTax: Number(cityTax.toFixed(2)),
    accommodation: Number(accommodation.toFixed(2)),
  };
},

async _amendAccommodationPrices(
  res: ReservationModel,
  perNightGrossPrices: number[],
  targetTotal: number,
  currency: string,
  log: LogFn,
): Promise<{ postAmendTotal: number | string | null }> {
  // --- Step 1: Initial amend with the target per-night prices ---
  const amendTimeSlices = (res.timeSlices || []).map((ts, idx) => {
    const ratePlanId = ts?.ratePlan?.id || ts?.ratePlanId || null;
    if (!ratePlanId) {
      throw new Error(
        `Missing ratePlanId on timeSlice ${idx} of reservation ${res.id}`,
      );
    }
    const newGross = perNightGrossPrices[idx] ?? 0;
    const newNet = Number((newGross / 1.07).toFixed(2));
    return {
      ratePlanId,
      baseAmount: {
        grossAmount: newGross,
        netAmount: newNet,
        vatType: "Reduced",
        vatPercent: 7.0,
        currency,
      },
      totalGrossAmount: {
        amount: newGross,
        currency,
      },
    };
  });
  // Preserve childrenAges (and any other stay-level fields) so the
  // amend does not silently strip them.
  await this.amendReservation(res.id || "", {
    arrival: res.arrival,
    departure: res.departure,
    adults: res.adults,
    childrenAges: Array.isArray(res.childrenAges) ? res.childrenAges : [],
    timeSlices: amendTimeSlices,
  });
  log(
    "log",
    `Amended ${res.id} (step 1/2): accommodation set across ${amendTimeSlices.length} night(s)`,
  );

  // --- Step 2: Re-fetch and correct if total has 1-cent drift ---
  // Use `payableAmount.guest.amount` for the post-amend total because
  // it's the canonical "what the guest pays" total (accommodation +
  // city tax), matching the imported/original reservation total.
  // `totalAmount` is not always returned by the API.
  const postAmendRes = await this.fetchReservationById(
    res.id || "",
    "timeSlices,payableAmount",
  );
  const postAmendSlices = postAmendRes?.timeSlices || [];
  const lastIdx = postAmendSlices.length - 1;
  const postAmendTotal =
    postAmendRes?.payableAmount?.guest?.amount ??
    postAmendRes?.totalAmount?.amount ??
    postAmendRes?.totalGrossAmount?.amount ??
    null;
  const totalDriftCents =
    typeof postAmendTotal === "number"
      ? Math.round((postAmendTotal - targetTotal) * 100)
      : 0;

  if (Math.abs(totalDriftCents) > 1) {
    // Large drift (>> the ±1¢ the two-step amend can absorb): the folio
    // does NOT match the imported total. The projected breakdown assumed
    // the compliant Berlin model (city-tax line carries 7% VAT — senate
    // print: the ÜnSt is part of the 7%-VAT remuneration). If Apaleo's
    // CITY-TAX rate plan is configured with vatType Null (0% VAT), its
    // auto-derivation produces (accom/1.07)×0.075 WITHOUT VAT on the
    // tax line, and the total falls short by tax × 0.07 per night.
    // That is an APALEO CONFIG mismatch, not a script bug — surface it
    // loudly so the operator can fix the rate plan and re-run.
    log(
      "error",
      `Amended ${res.id}: UNRESOLVED total drift ${totalDriftCents > 0 ? "+" : ""}${(totalDriftCents / 100).toFixed(2)} ${currency} (post-amend ${typeof postAmendTotal === "number" ? postAmendTotal.toFixed(2) : "?"} vs target ${targetTotal.toFixed(2)}). Expected ±1¢ only. ` +
      `Likely cause: the Apaleo CITY-TAX rate plan derives the tax WITHOUT 7% VAT (vatType Null). The Berlin senate print requires the city tax to carry 7% VAT. Fix the rate plan's VAT type in the Apaleo portal, then re-run this tool on the affected reservations.`,
    );
  } else if (Math.abs(totalDriftCents) === 1) {
    // Total is off by 1 cent. Reduce the last night's gross by 1 cent
    // and send a corrective amend. Apaleo honors the explicit net on
    // the second amend because the net→gross derivation produces a
    // value that can be stored without further re-rounding.
    const sentLastGross = perNightGrossPrices[lastIdx] ?? 0;
    const correctedGross = Number(
      ((sentLastGross * 100 - totalDriftCents) / 100).toFixed(2),
    );
    const correctedNet = Number((correctedGross / 1.07).toFixed(2));
    const correctiveTimeSlices = postAmendSlices.map((ts, idx) => {
      const ratePlanId = ts?.ratePlan?.id || ts?.ratePlanId || null;
      if (idx !== lastIdx) {
        return {
          ratePlanId,
          baseAmount: {
            grossAmount: ts.baseAmount?.grossAmount,
            netAmount: ts.baseAmount?.netAmount,
            vatType: ts.baseAmount?.vatType || "Reduced",
            vatPercent: ts.baseAmount?.vatPercent ?? 7.0,
            currency,
          },
          totalGrossAmount: {
            amount: ts.totalGrossAmount?.amount ?? ts.baseAmount?.grossAmount,
            currency,
          },
        };
      }
      return {
        ratePlanId,
        baseAmount: {
          grossAmount: correctedGross,
          netAmount: correctedNet,
          vatType: "Reduced",
          vatPercent: 7.0,
          currency,
        },
        totalGrossAmount: {
          amount: correctedGross,
          currency,
        },
      };
    });
    // Preserve childrenAges from the original reservation (see Step 1
    // comment for context). Same reasoning: the amend endpoint
    // expects the FULL stay payload.
    await this.amendReservation(res.id || "", {
      arrival: res.arrival,
      departure: res.departure,
      adults: res.adults,
      childrenAges: Array.isArray(res.childrenAges) ? res.childrenAges : [],
      timeSlices: correctiveTimeSlices,
    });
    log(
      "log",
      `Amended ${res.id} (step 2/2): corrected last night gross from ${sentLastGross.toFixed(2)} to ${correctedGross.toFixed(2)} (net ${correctedNet.toFixed(2)}) to absorb ${totalDriftCents > 0 ? "+" : ""}${totalDriftCents} cent total drift`,
    );
  } else {
    log(
      "log",
      `Amended ${res.id}: no total drift detected (post-amend total ${typeof postAmendTotal === "number" ? postAmendTotal.toFixed(2) : "unknown"} vs target ${targetTotal.toFixed(2)}, drift ≤ 1¢)`,
    );
  }

  return { postAmendTotal };
},

/**
 * Creates an additional folio for a reservation (or an external folio).
 * Endpoint: POST /finance/v1/folios
 *
 * Ported from the pre-split src/services/apaleo.ts monolith when the
 * feature/ui-ux-cleanup branch was merged into main.
 */
async createFolio(payload: {
  reservationId?: string;
  type?: 'Guest' | 'External';
  debitor?: Record<string, unknown>;
  companyId?: string;
  propertyId?: string;
}): Promise<{ id: string }> {
  if (!payload?.reservationId && !payload?.propertyId && !payload?.companyId) {
    throw new Error("createFolio: reservationId, propertyId or companyId is required");
  }
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Idempotency-Key": `folio-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  };
  return (await this._fetch(`/finance/v1/folios`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      type: payload.type || "Guest",
      ...payload,
    }),
  })) as { id: string };
},

/**
 * Moves multiple charges/allowances from one folio to another.
 * Endpoint: PUT /finance/v1/folio-actions/{folioId}/move-charges
 */
async moveCharges(
  sourceFolioId: string,
  payload: { targetFolioId: string; reason: string; chargeIds: string[] },
): Promise<unknown> {
  if (!sourceFolioId) throw new Error("moveCharges: sourceFolioId is required");
  if (!payload?.targetFolioId) throw new Error("moveCharges: targetFolioId is required");
  return await this._fetch(
    `/finance/v1/folio-actions/${encodeURIComponent(sourceFolioId)}/move-charges`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
},

/**
 * Moves all charges from one folio to another.
 * Endpoint: PUT /finance/v1/folio-actions/{folioId}/move-all-charges
 */
async moveAllCharges(
  sourceFolioId: string,
  payload: { targetFolioId: string },
): Promise<unknown> {
  if (!sourceFolioId) throw new Error("moveAllCharges: sourceFolioId is required");
  if (!payload?.targetFolioId) throw new Error("moveAllCharges: targetFolioId is required");
  return await this._fetch(
    `/finance/v1/folio-actions/${encodeURIComponent(sourceFolioId)}/move-all-charges`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
},

};

export default part;
